import { useState } from "react";
import { CopyButton } from "@/features/ordini/components/OrderCard";
import { nomeCorriere } from "@/features/ordini/nomeCorriere";
import { KindBadge, StatusBadge } from "@/features/resi/components/PraticaBadge";
import { openReturnLabel } from "@/features/resi/labelDownload";
import { prettifyApiError } from "@/shared/api";
import { formatDateIt, formatDateTime } from "@/shared/format";
import { openExternalUrl } from "@/shared/openExternalUrl";
import { TrackingPanel } from "../TrackingPanel";
import { TrackingTimeline } from "../TrackingTimeline";
import { ApriTracking, Riga, Righe, Sezione, Vuoto } from "./parti";
import { PRATICA_APERTA, type ContextOrder, type ParcelInfo, type ReturnSummary } from "./tipi";

/** Codice da seguire: si legge, si copia, si apre sul sito del corriere. */
function Codice({ codice, url }: { codice: string; url?: string | null }) {
  return (
    <span className="flex min-w-0 items-center gap-1.5">
      <span className="truncate tabular-nums text-[13.5px] font-semibold text-ink" title={codice}>
        {codice}
      </span>
      <CopyButton text={codice} label="Copia codice tracking" />
      {url ? <ApriTracking url={url} codice={codice} /> : null}
    </span>
  );
}

/** La spedizione dell'ordine al cliente. Lo stato e' l'ultimo evento del
 *  corriere gia' in archivio (job orario): aprire una chat non lo interroga,
 *  lo storico completo si legge al click sulla pill. */
export function SpedizioneOriginale({
  order,
  parcels,
  trackingUrl,
  titolo,
}: {
  order: ContextOrder;
  parcels: ParcelInfo[];
  trackingUrl: string;
  titolo: string;
}) {
  const ev = order.last_tracking_event;
  const colli = parcels.filter((p) => p.code && p.code !== order.tracking_code);
  const dove = ev ? [ev.location, formatDateTime(ev.at ?? null)].filter(Boolean).join(" · ") : "";
  return (
    <Sezione titolo={titolo}>
      {order.tracking_code ? (
        <>
          <Codice codice={order.tracking_code} url={trackingUrl} />
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <TrackingTimeline
              variant="inline"
              statusRaw={order.status}
              carrier={order.carrier}
              trackingCode={order.tracking_code}
              parcels={parcels}
              onOpenExternal={openExternalUrl}
              lastCourierEvent={
                ev?.label
                  ? {
                      label: ev.label,
                      delivered: ev.delivered === true,
                      ritornoMittente: ev.ritorno_mittente === true,
                    }
                  : null
              }
            />
            {dove && <span className="text-[12px] text-ink">{dove}</span>}
          </div>
          {colli.length > 0 && (
            <div className="space-y-0.5 border-t border-line pt-1.5">
              <div className="text-[11px] font-semibold text-ink">Colli ({colli.length + 1})</div>
              {colli.map((p) => (
                <div key={p.code} className="flex items-center gap-1.5">
                  <span className="truncate tabular-nums text-[11.5px] text-ink" title={p.code}>
                    {p.code}
                  </span>
                  <CopyButton text={p.code} label="Copia codice collo" />
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        <Vuoto>Etichetta non ancora fatta</Vuoto>
      )}
      <Righe>
        <Riga label="Corriere">{nomeCorriere(order.carrier)}</Riga>
      </Righe>
    </Sezione>
  );
}

/** Dove va il pezzo, in una riga: chi e citta'. Su una sostituzione e'
 *  spesso un indirizzo terzo, diverso da quello dell'ordine. */
function destinatarioBreve(a: ReturnSummary["recipient"]): string {
  if (!a) return "";
  const chi = a.company || [a.name, a.surname].filter(Boolean).join(" ");
  const dove = a.city && a.state_iso ? `${a.city} (${a.state_iso})` : a.city || "";
  return [chi, dove].filter(Boolean).join(" · ");
}

/** Stato del corriere di una pratica chiusa: lettura dal vivo solo al click. */
function TrackingAlClick({ codes }: { codes: string[] }) {
  const [aperto, setAperto] = useState(false);
  return (
    <div>
      <button
        type="button"
        onClick={() => setAperto((v) => !v)}
        aria-expanded={aperto}
        className="text-[12px] font-semibold text-accent-2 hover:underline"
      >
        {aperto ? "Nascondi stato spedizione" : "Vedi stato spedizione"}
      </button>
      {aperto && (
        <div className="mt-1.5 rounded-md border border-line bg-paper p-2.5">
          {/* ponytail: "SDA" e' solo etichetta: la fonte la sceglie il backend dal codice. */}
          <TrackingPanel
            carrier="SDA"
            trackingCode={codes[0] ?? ""}
            parcelCodes={codes}
            onOpenExternal={openExternalUrl}
          />
        </div>
      )}
    </div>
  );
}

/** L'etichetta della pratica, sempre a portata (Davide 16/10: reso creato
 *  dalla chat, chat chiusa prima di scaricarla, LDV non piu' raggiungibile). */
function ScaricaEtichetta({ id, kind }: { id: number; kind: string }) {
  const [busy, setBusy] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  async function scarica() {
    setBusy(true);
    setErrore(null);
    try {
      await openReturnLabel({ id, label_path: null });
    } catch (e) {
      setErrore(prettifyApiError(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <div>
      <button
        type="button"
        onClick={() => void scarica()}
        disabled={busy}
        aria-busy={busy}
        className="rounded-sm border border-line-2 bg-card px-2 py-[5px] text-[12px] font-semibold text-ink hover:border-accent hover:bg-paper disabled:opacity-50"
      >
        {busy
          ? "Scarico…"
          : `Scarica etichetta ${kind === "sostituzione" ? "sostituzione" : "reso"}`}
      </button>
      {errore && <div className="mt-1 text-[12px] text-red">{errore}</div>}
    </div>
  );
}

function Pratica({ r }: { r: ReturnSummary }) {
  const tracking = r.tracking_numbers[0];
  const dove = destinatarioBreve(r.recipient);
  const aperta = PRATICA_APERTA.has(r.status);
  return (
    <div className="flex flex-col gap-1.5 border-t border-line pt-2 first:border-t-0 first:pt-0">
      <div className="flex flex-wrap items-center gap-1">
        <KindBadge kind={r.kind} />
        <StatusBadge status={r.status} />
      </div>
      {tracking ? (
        aperta ? (
          // Pratica in viaggio: lo stato del corriere si legge da solo
          // all'apertura (Davide 2026-10-14, «devo poterla seguire al volo»).
          <>
            <Codice codice={tracking} url={r.tracking_url} />
            <TrackingPanel
              compatto
              nascondiCodice
              carrier="SDA"
              trackingCode={tracking}
              parcelCodes={r.tracking_numbers}
              onOpenExternal={openExternalUrl}
            />
          </>
        ) : (
          <>
            <Codice codice={tracking} url={r.tracking_url} />
            <TrackingAlClick codes={r.tracking_numbers} />
          </>
        )
      ) : (
        <Vuoto>Tracking non ancora assegnato</Vuoto>
      )}
      {r.has_label && r.id != null && <ScaricaEtichetta id={r.id} kind={r.kind} />}
      <Righe>
        {dove && <Riga label="Va a">{dove}</Riga>}
        {r.created_at && <Riga label="Creata il">{formatDateIt(r.created_at)}</Riga>}
      </Righe>
    </div>
  );
}

/** Resi e sostituzioni dell'ordine. Con una pratica ancora in viaggio il
 *  blocco sale in cima e si accende: e' la domanda tipica della chat. */
export function Pratiche({ returns, evidenza }: { returns: ReturnSummary[]; evidenza: boolean }) {
  const titolo = evidenza
    ? returns.some((r) => r.kind === "sostituzione" && PRATICA_APERTA.has(r.status))
      ? "Sostituzione in viaggio"
      : "Reso in corso"
    : "Resi e sostituzioni";
  return (
    <Sezione titolo={titolo} evidenza={evidenza}>
      {returns.length === 0 ? (
        <Vuoto>Nessun reso o sostituzione</Vuoto>
      ) : (
        returns.map((r, i) => <Pratica key={`${r.kind}-${r.created_at ?? i}`} r={r} />)
      )}
    </Sezione>
  );
}
